import type { ResultadoEmissaoData } from '../../components/orcamento/ModalResultadoEmissao';

/**
 * A nota que o SERVIDOR emitiu sozinho depois que a venda finalizou. [DA-584]
 *
 * Cerco do Pix ([DA-248]): quando a venda fecha `aguardando_nota`, o backend ainda tenta emitir UMA
 * vez logo depois de responder ao front (`dispararEmissaoSeConciliada`, fora da transacao). Quando
 * ele acerta, a nota nasce AUTORIZADA sem que a tela saiba: a vendedora fica com o aviso "a nota sai
 * quando o pagamento for confirmado" e SEM o painel de impressao, com o cliente esperando o cupom.
 *
 * Medido em 19/08/2026 (ORC-006880): venda finalizada 14:56:39,07 e `[cerco-pix] nfce AUTORIZADA
 * n1704` em 14:56:39,46 — 0,4 segundo depois. Por isso a tela espera alguns segundos por essa nota
 * antes de desistir; quando ela sai, sai quase junto.
 */

/** Linha da nota como as rotas `GET /api/{nfce|nfe}/orcamento/:id` devolvem. */
export interface NotaDoOrcamento {
  id: number;
  chave?: string | null;
  numero?: number | null;
  serie?: string | null;
  status: string;
  protocolo?: string | null;
  codigo_status?: string | null;
  motivo_status?: string | null;
  qr_code_url?: string | null;
  valor_total?: string | number | null;
}

/**
 * Traduz a nota AUTORIZADA (se houver) pro formato que o painel de impressao le. Devolve null
 * quando nao ha nenhuma autorizada — rejeitada, cancelada ou em erro NAO abrem o painel de
 * sucesso: quem finalizou precisa continuar vendo o aviso de que a nota ainda nao saiu.
 */
export function mapearNotaAutorizada(notas: NotaDoOrcamento[] | null | undefined): ResultadoEmissaoData | null {
  const nota = (notas ?? []).find((n) => n?.status === 'autorizada');
  if (!nota) return null;
  return {
    id: nota.id,
    status: nota.status,
    numero: nota.numero ?? undefined,
    serie: nota.serie ?? undefined,
    protocolo: nota.protocolo ?? undefined,
    chave: nota.chave ?? undefined,
    cStat: nota.codigo_status ?? undefined,
    xMotivo: nota.motivo_status ?? undefined,
    qrCodeUrl: nota.qr_code_url ?? undefined,
    autorizada: true,
    valorTotal: nota.valor_total != null ? Number(nota.valor_total) : undefined,
  };
}

/**
 * Espera a nota do cerco aparecer, consultando `buscar` algumas vezes. Para no primeiro acerto.
 *
 * ⚠️ Erro em uma tentativa NAO interrompe a espera: a venda ja esta finalizada, e uma oscilacao de
 * rede no meio nao pode virar erro na cara de quem esta com o cliente no balcao — a nota, se saiu,
 * continua impressa pela tela de Notas Fiscais.
 *
 * ⚠️ E' proposital esperar POUCO: quando o pagamento falta de verdade, a nota so sai quando a
 * conciliacao bater (minutos ou horas), a vendedora ja esta no proximo atendimento, e um painel
 * abrindo sozinho la na frente cairia por cima de outra venda.
 */
export async function esperarNotaAutorizada(
  buscar: () => Promise<NotaDoOrcamento[]>,
  opcoes: { tentativas?: number; intervaloMs?: number; dormir?: (ms: number) => Promise<void> } = {},
): Promise<ResultadoEmissaoData | null> {
  const tentativas = opcoes.tentativas ?? 8;
  const intervaloMs = opcoes.intervaloMs ?? 1000;
  const dormir = opcoes.dormir ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let i = 0; i < tentativas; i++) {
    await dormir(intervaloMs);
    try {
      const achada = mapearNotaAutorizada(await buscar());
      if (achada) return achada;
    } catch {
      // segue tentando — ver o aviso acima
    }
  }
  return null;
}
